import styles from '@/components/tiptap/shared/styles/index.module.scss';
import { useBlockEditorMarkdown } from '@/components/tiptap/vMarkdown/useBlockEditor';
import { simpleMarkdownToHtml } from '@/helpers/markdownToHtml';
import { cn } from '@/lib/cn';
import { TypographyCaption } from '@heidi/design-system/typography';
import { EditorContent } from '@tiptap/react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

export interface DraftNoteBodyProps {
  markdown: string;
  isStreaming?: boolean;
  comparison?: {
    before: string;
    after: string;
  };
}

/**
 * Read-only scribe-note TipTap preview. Same editor + ProseMirror styles as
 * Ask Heidi's ``UserNotesBlock`` — the Work draft card cannot import Chat, so
 * this is the shared render path.
 */
const DraftMarkdownPreview = ({
  markdown,
  isStreaming,
}: {
  markdown: string;
  isStreaming: boolean;
}) => {
  const htmlContent = useMemo(() => {
    if (!markdown) return '';
    const normalizedText = markdown.replace(/\n{3,}/g, '\n\n');
    return simpleMarkdownToHtml({ markdown: normalizedText });
  }, [markdown]);

  const { editor } = useBlockEditorMarkdown({
    content: htmlContent,
    editable: false,
    placeholder: '',
    isStreaming,
    surface: 'document',
  });

  if (!htmlContent) return null;

  return (
    <EditorContent
      editor={editor}
      className={cn(styles.ProseMirror, '[&_.ProseMirror]:outline-none')}
    />
  );
};

export const DraftNoteBody = ({
  markdown,
  isStreaming = false,
  comparison,
}: DraftNoteBodyProps) => {
  if (!comparison) {
    return <DraftMarkdownPreview markdown={markdown} isStreaming={isStreaming} />;
  }

  return (
    <div className="grid gap-4">
      {comparison.before ? (
        <section className="grid gap-1.5 rounded-md border border-border bg-surface-page p-3">
          <TypographyCaption className="font-medium text-text-secondary">
            <FormattedMessage
              defaultMessage="Before"
              id="pW/vgZyO3/"
              description="Label for original text in a draft edit comparison"
            />
          </TypographyCaption>
          <DraftMarkdownPreview markdown={comparison.before} isStreaming={false} />
        </section>
      ) : null}
      {comparison.after ? (
        <section className="grid gap-1.5 rounded-md border border-border bg-surface-page p-3">
          <TypographyCaption className="font-medium text-text-secondary">
            <FormattedMessage
              defaultMessage="After"
              id="EU9LnU33DU"
              description="Label for proposed text in a draft edit comparison"
            />
          </TypographyCaption>
          <DraftMarkdownPreview markdown={comparison.after} isStreaming={isStreaming} />
        </section>
      ) : null}
    </div>
  );
};
